/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { CheckCircle2, Circle, Star } from 'lucide-react';

const roadmapData = {
    'Q4 2025': [
        { text: 'Cross-Chain DEX Aggregator', status: 'done' },
        { text: 'Advanced AI Analytics Launch', status: 'done' },
        { text: 'Token-2022 Extensions Support', status: 'done' },
    ],
    'Q1 & Q2 2026': [
        { text: 'Mobile SDKs (Alpha)', status: 'in-progress' },
        { text: 'On-chain Governance v1', status: 'in-progress' },
        { text: 'L2 Integration Research', status: 'planned' },
        { text: 'Expanded Fiat On-ramps', status: 'planned' },
    ],
    '2026 & Beyond': [
        { text: 'Fully Decentralized AI Engine (Swarm v2)', status: 'future' },
        { text: 'Native Cross-Chain Bridge Integration', status: 'future' },
        { text: 'Expansion to New VM Ecosystems', status: 'future' },
    ],
};

const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'done') {
        return <CheckCircle2 size={20} className="text-green-500" />;
    }
    if (status === 'future') {
        return (
            <div className="relative w-5 h-5 flex items-center justify-center">
                 <Star size={20} className="text-nobel-gold fill-current" />
            </div>
        );
    }
    if (status === 'in-progress') {
        return (
            <div className="relative w-5 h-5 flex items-center justify-center">
                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                 <Circle size={8} className="text-blue-500 fill-current" />
            </div>
        );
    }
    return <Circle size={20} className="text-stone-300" />;
};

export const Roadmap: React.FC = () => {
    return (
        <section className="bg-transparent">
            <div className="mx-auto">
                <div className="text-center max-w-2xl mx-auto mb-16">
                    <span className="text-xs font-bold tracking-widest text-nobel-gold uppercase mb-2 block">Future of HotLaunch</span>
                    <h2 className="font-serif text-4xl text-stone-900 mb-4 flex items-center justify-center gap-3">
                        Roadmap to Decentralization
                    </h2>
                    <p className="text-stone-500 max-w-xl mx-auto">
                        Our strategic vision for expanding the ecosystem, enhancing AI capabilities, and achieving full community governance.
                    </p>
                </div>

                <div className="relative max-w-3xl mx-auto bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
                    {/* Vertical Timeline Line */}
                    <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-stone-200"></div>

                    {Object.entries(roadmapData).map(([quarter, items]) => (
                        <div key={quarter} className="relative pl-16 pb-16 last:pb-0">
                            {/* Quarter Marker */}
                            <div className="absolute left-4 top-1.5 transform -translate-x-1/2">
                                <div className="w-8 h-8 bg-stone-900 rounded-full flex items-center justify-center text-white font-bold text-xs ring-8 ring-white shadow-md z-10">
                                    {quarter.split(' ')[0]}
                                </div>
                            </div>
                            
                            <h3 className="font-serif text-2xl font-bold text-stone-900 mb-6">{quarter}</h3>
                            
                            <div className="space-y-6">
                                {items.map((item, index) => (
                                    <div key={index} className="flex items-start gap-4">
                                        <div className="flex-shrink-0 mt-0.5">
                                            <StatusIcon status={item.status} />
                                        </div>
                                        <div>
                                            <p className={`font-bold text-stone-800 ${item.status === 'done' ? 'line-through text-stone-400' : ''}`}>
                                                {item.text}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};